import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Select from 'components/form/Select';
import TextField from 'components/form/TextInput';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import CopyToClipboard from 'react-copy-to-clipboard';
import { snack } from 'utils/toast';
import IconButton from 'components/IconButton';
import Button from 'components/Button';
import CopyIcon from '@mui/icons-material/ContentCopyOutlined';
import Form from 'components/form';
import { withHttp, validateUrl } from 'utils';
import { STAGE } from 'constants/config';

const { FormItem } = Form;

const DOMAINS = ['churchqrco.de', 'into.church', 'form.church', 'ticsms.co', 'churchvid.io'];

const DOMAIN_OPTIONS = DOMAINS.map((domain) => ({
  label: STAGE === 'prod' ? domain : `qa.${domain}`,
  value: STAGE === 'prod' ? domain : `qa.${domain}`,
}));

const normalize = (value) => {
  if (!value) return value;
  return value.replace(/[^a-zA-Z0-9_-]/g, '');
};

const parsePrefix = (str) => {
  if (!str) return {};
  const parts = str.split('/');
  const suffix = parts.pop();
  const prefix = parts.join('/');
  return { prefix, suffix };
};

const CustomizeUrl = ({ onClose, value, form, onConfirm }) => {
  const [t] = useTranslation('urlshortener');
  const { getFieldDecorator, validateFields, getFieldsValue, setFieldsValue } =
    form;
  const values = getFieldsValue(['redir_domain', 'prefix', 'redir_url_short']);

  useEffect(() => {
    if (value) {
      const { prefix, suffix } = parsePrefix(value.redir_url_short);
      setFieldsValue({
        prefix,
        redir_url_short: suffix,
        redir_domain: value.redir_domain,
        redir_name: value.redir_name,
      });
    }
  }, []);

  const onSubmit = () => {
    return validateFields().then(async (values) => {
      try {
        const { prefix = '', redir_url_short } = values;
        const length = redir_url_short.length + prefix.length + 1;
        if (length > 64) {
          throw new Error(
            'Short URL including prefix must be less than 64 characters',
          );
        }
        if (value) {
          values.redir_id = value.redir_id;
        }
        await onConfirm(values);
        onClose();
      } catch (e) {
        snack.error(e.message);
      }
    });
  };

  const previewUrl = useMemo(() => {
    if (!values) return '';
    const { redir_domain, prefix, redir_url_short } = values;
    if (!redir_domain) return null;
    const domain = withHttp(redir_domain);
    return [domain, prefix, redir_url_short].filter((i) => i).join('/');
  }, [values]);

  return (
    <Box textAlign="left">
      <Form>
        {!value && (
          <Box mb={2}>
            <FormItem>
              {getFieldDecorator('redir_url_long', {
                rules: [
                  { required: true, message: t('common:field-required') },
                  { validator: validateUrl() },
                ],
              })(
                <TextField
                  fullWidth
                  type="url"
                  placeholder="Paste your URL here to shorten it"
                />,
              )}
            </FormItem>
          </Box>
        )}
        <Typography fontWeight={500} variant="subtitle1">
          {t('customize-title', 'Customize my URL')}
        </Typography>
        <Typography paragraph color="text.secondary">
          {t(
            'customize-description',
            'Customize your URL to make it memorable',
          )}
        </Typography>

        <FormItem>
          {getFieldDecorator('redir_name', {
            rules: [{ required: true, message: t('common:field-required') }],
          })(
            <TextField
              fullWidth
              label="Title"
              tooltip={t('url-name-tooltip', 'Give a name to your short URL')}
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('redir_domain', {
            rules: [{ required: true, message: t('common:field-required') }],
          })(
            <Select
              fullWidth
              transformValue
              label="Set default root URL"
              tooltip={t(
                'select-domain-tooltip',
                'This is the beginning of your URL (eg. “https://textinchurch.com/...)',
              )}
              options={DOMAIN_OPTIONS}
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('prefix', {
            normalize: normalize,
          })(
            <TextField
              fullWidth
              label="Optional Prefix"
              tooltip={t('url-prefix-tooltip', '')}
            />,
          )}
        </FormItem>
        <FormItem>
          {getFieldDecorator('redir_url_short', {
            normalize: normalize,
            rules: [{ required: true, message: t('common:field-required') }],
          })(
            <TextField
              fullWidth
              label="Edit slug"
              tooltip={t(
                'slug-tooltip',
                'This is the tail end of your URL, which could refer to the content of it (eg. “...kansascitychurch/xmasholidays”)',
              )}
            />,
          )}
        </FormItem>

        {value?.qr_code_text && (
          <Alert severity="warning">
            {t(
              'slug-edit-warning',
              'Changing slug or prefix will make your existing QR code invalid.',
            )}
          </Alert>
        )}
        {previewUrl && (
          <Box
            textAlign="center"
            mt={2}
            bgcolor="background.default"
            borderRadius={1}
            p={2}
          >
            <Typography gutterBottom>Link preview</Typography>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Typography color="primary.main">{previewUrl}</Typography>
              <CopyToClipboard
                text={previewUrl}
                onCopy={() => snack.success('Link copied to clipboard.')}
              >
                <IconButton
                  size="small"
                  icon={<CopyIcon fontSize="inherit" />}
                />
              </CopyToClipboard>
            </Stack>
          </Box>
        )}
        <Stack mt={4} direction="row" justifyContent="center" spacing={1}>
          <Button onClick={onClose} variant="text">
            Cancel
          </Button>
          <Button onClick={onSubmit}>Save</Button>
        </Stack>
      </Form>
    </Box>
  );
};

export default Form.create()(CustomizeUrl);
